/**
 * ISG Business Intelligence — Frontend Entry Point
 *
 * Generated from ISG App Template.
 * Uses @isg/ui-kit for shared CSS, components, and services.
 */

// --- Styles (from @isg/ui-kit) ---
import '@isg/ui-kit/css/tokens.css';
import '@isg/ui-kit/css/reset.css';
import '@isg/ui-kit/css/components.css';
import '@isg/ui-kit/css/layout.css';
import './styles/bi.css';

// --- Services & Utils (from @isg/ui-kit barrel) ---
import { router, store, auth , checkEnvBanner } from '@isg/ui-kit';
import { renderNavigation, renderMobileHeader, createSidebarOverlay } from '@isg/ui-kit';
import { applyBranding, loadBranding } from '@isg/ui-kit';

/* SSO: capture the handoff from the intranet broker.
 *
 * MUST stay at module top level, before init() — the broker returns the user to
 * /auth/sso-callback?handoff=…, and this is what exchanges that single-use JWT
 * for auth cookies and navigates onward. Without it the page is BLANK, not
 * broken-looking: ui-kit's checkSession() deliberately returns a never-resolving
 * promise on that path so it cannot race the exchange, so `await
 * auth.checkSession()` in init() simply hangs and nothing ever renders.
 *
 * That is why it must be a top-level call and not a router entry — by the time
 * a route resolves, init() has already awaited checkSession() and stalled.
 */
auth.captureSSOHandoff();

// --- Lazy-loaded pages ---
const pages = {
  ask:       () => import('./pages/ask.js'),
  builder:   () => import('./pages/builder.js'),
  catalogue: () => import('./pages/catalogue.js'),
  dashboard: () => import('./pages/dashboard.js'),
  directors: () => import('./pages/directors.js'),
  markets: () => import('./pages/markets.js'),
  plan: () => import('./pages/plan.js'),
  huddle:    () => import('./pages/huddle.js'),
  huddleEdit: () => import('./pages/huddle-edit.js'),
  huddleFit: () => import('./pages/huddle-fit.js'),
  login:     () => import('./pages/login.js'),
  // Add more pages here:
  // myFeature: () => import('./pages/my-feature.js'),
};

// ── Navigation config ────────────────────────────────────────────────
// Sections group nav items. Use section: null for top-level items.
const NAV_CONFIG = [
  {
    section: null,
    items: [
      { path: '/huddle', label: 'Daily Huddle', icon: 'presentation-chart-line' },
      { path: '/directors', label: 'CEO Dashboard', icon: 'trending-up' },
      { path: '/markets', label: 'Markets', icon: 'globe' },
      { path: '/plan', label: 'Plan', icon: 'target' },
      { path: '/dashboard', label: 'Dashboard', icon: 'layout-dashboard' },
      { path: '/', label: 'Ask', icon: 'message-circle-question' },
      { path: '/builder', label: 'Builder', icon: 'blocks' },
      { path: '/catalogue', label: 'Catalogue', icon: 'table' },
    ],
  },
  /* Per-app admin functions live IN the app, for admins (estate convention —
   * isg-admin is estate-wide administration, not a dumping ground). ui-kit
   * filters adminOnly ITEMS and skips emptied sections; the API behind this
   * refuses non-admins regardless — the nav filter is presentation, not the
   * boundary. */
  {
    section: 'Admin',
    items: [
      { path: '/huddle/edit', label: 'Deck editor', icon: 'layout-grid', adminOnly: true },
    ],
  },
  // Add more sections and items here:
  // {
  //   section: 'Features',
  //   items: [
  //     { path: '/my-feature', label: 'My Feature', icon: 'file-text' },
  //   ],
  // },
];

/* The page currently mounted, so its `destroy()` can run before the next one
 * mounts. Added 2026-08-12 with the huddle, which is the first page here that
 * owns anything outliving its DOM: a 60-second presentation-mode `setInterval`
 * and two document-level keyboard listeners.
 *
 * Without this, navigating away from the huddle in presentation mode leaves a
 * timer polling the ERP every minute for a page nobody is looking at — invisible,
 * indefinite, and it survives every subsequent navigation. `destroy()` is
 * OPTIONAL; pages that own nothing simply do not export it. */
let _mountedPage = null;

// ── Render a page module into #main-content ──────────────────────────
async function renderPage(loader, params = {}) {
  const main = document.getElementById('main-content');
  if (!main) return;

  if (_mountedPage && typeof _mountedPage.destroy === 'function') {
    try {
      _mountedPage.destroy();
    } catch (err) {
      // A page that fails to clean up must not block the next one rendering.
      console.error('Page destroy error:', err);
    }
  }
  _mountedPage = null;

  main.innerHTML = `
    <div style="display:flex;align-items:center;justify-content:center;min-height:50vh">
      <div class="spinner"></div>
    </div>
  `;

  try {
    const module = await loader();
    main.innerHTML = '';
    _mountedPage = module;
    await module.render(main, params);
  } catch (err) {
    console.error('Page render error:', err);
    main.innerHTML = `
      <div class="main-content-inner">
        <div class="empty-state">
          <p style="color: var(--red-600); font-weight: 500;">Something went wrong</p>
          <p>${err.message || 'Failed to load page.'}</p>
          <button class="btn btn-primary mt-4" onclick="location.reload()">Reload</button>
        </div>
      </div>
    `;
  }
}

// ── App shell ────────────────────────────────────────────────────────
function renderAppShell() {
  const app = document.getElementById('app');
  app.innerHTML = '';

  // Accent stripe at top
  const stripe = document.createElement('div');
  stripe.className = 'accent-stripe';
  app.appendChild(stripe);

  const layout = document.createElement('div');
  layout.className = 'app-layout';

  const mobileHeader = renderMobileHeader({ appTitle: 'ISG Business Intelligence' });
  const overlay = createSidebarOverlay();

  const sidebar = renderNavigation({
    navConfig: NAV_CONFIG,
    appTitle: 'ISG Business Intelligence',
    fallbackInitial: 'I',
  });

  const main = document.createElement('main');
  main.className = 'main-content';
  main.id = 'main-content';

  layout.appendChild(mobileHeader);
  layout.appendChild(overlay);
  layout.appendChild(sidebar);
  layout.appendChild(main);
  app.appendChild(layout);

}

// ── Init ─────────────────────────────────────────────────────────────
async function init() {
  // Pre-load branding so sidebar logo is ready
  await loadBranding().catch(() => {});
  const hasSession = await auth.checkSession();

  // Define routes
  router
    .on('/', (params) => renderPage(pages.ask, params))
    .on('/builder', (params) => renderPage(pages.builder, params))
    .on('/catalogue', (params) => renderPage(pages.catalogue, params))
    .on('/dashboard', (params) => renderPage(pages.dashboard, params))
    .on('/huddle', (params) => renderPage(pages.huddle, params))
    .on('/directors', (params) => renderPage(pages.directors, params))
    .on('/markets', (params) => renderPage(pages.markets, params))
    // the division one-pager lives on Markets (its landing view) since 2026-10-26; old links land there
    .on('/glance', () => { try { sessionStorage.setItem('markets_view', 'glance'); } catch { /* private mode */ } router.navigate('/markets'); })
    .on('/plan', (params) => renderPage(pages.plan, params))
    /* The layout editor (admins; the API refuses everyone else) and the fit
     * harness its measurement iframes load. Neither is in the nav — the
     * editor is reached from the huddle page's edit button. */
    .on('/huddle/edit', (params) => renderPage(pages.huddleEdit, params))
    .on('/huddle/fit', (params) => renderPage(pages.huddleFit, params))
    .on('/login', (params) => renderPage(pages.login, params));
    // Add more routes here:
    // .on('/my-feature', (params) => renderPage(pages.myFeature, params));

  // Auth guard — both directions.
  router.before((path) => {
    if (path === '/login') {
      /* An AUTHENTICATED user must never be left on the login page. The scaffold
       * returned true here unconditionally, which renders the login FORM inside
       * the app shell — the app looks signed in and signed out at once, and only
       * a manual reload clears it.
       *
       * It is not an edge case. The broker returns you to whatever `next` it was
       * carrying, and a failed exchange rewrites that to /login?sso_error=… — so
       * the FIRST successful login after any SSO problem lands here. Back button
       * and bookmarks do it too. */
      if (store.get('isAuthenticated')) {
        router.navigate('/');
        return false;
      }
      return true;
    }
    if (!store.get('isAuthenticated')) {
      router.navigate('/login');
      return false;
    }
    return true;
  });

  // Apply branding after every route change
  router.after(() => {
    setTimeout(() => applyBranding(), 50);
  });

  if (!hasSession) {
    // No session — show login
    const app = document.getElementById('app');
    app.innerHTML = '<main id="main-content" role="main"></main>';
    const module = await pages.login();
    /* AWAIT the render. `render()` is async — it fetches /auth/config before it
     * writes any markup — so without this, applyBranding() below runs against an
     * EMPTY container: `.login-hero-logo` doesn't exist yet, the selector misses,
     * and the page keeps the placeholder "I" instead of the ISG logo. Silent:
     * nothing errors, the branding call just finds nothing to brand. */
    await module.render(document.getElementById('main-content'));
    if (window.location.pathname !== '/login') {
      window.history.replaceState(null, '', '/login');
    }
    applyBranding();
  } else {
    // Authenticated — render app shell and resolve current route
    renderAppShell();
    checkEnvBanner(); // beta/test banner (no-op on live)
    await router.resolve();
    applyBranding();
  }
}

// Boot
init().catch((err) => {
  console.error('Failed to initialize app:', err);
  document.getElementById('app').innerHTML = `
    <div style="display:flex;align-items:center;justify-content:center;min-height:100vh;flex-direction:column;gap:1rem;">
      <p>Failed to load the application. Please refresh.</p>
      <button class="btn btn-primary" onclick="location.reload()">Reload</button>
    </div>
  `;
});
